<template>
  <div
    v-if="!dataLoad"
    class="find-request-wrapper"
  >
    <b-button
      variant="light"
      class="find-request-btn d-inline-flex align-items-center"
      @click="openModal"
    >
      <feather-icon
        icon="SearchIcon"
        size="14"
        class="mr-50"
      />
      <span class="text-nowrap">Find request</span>
    </b-button>

    <b-modal
      v-model="showModal"
      modal-class="fr-modal"
      content-class="fr-modal-content"
      body-class="fr-modal-body p-0"
      header-class="fr-modal-header"
      footer-class="fr-modal-footer"
      hide-header-close
      no-close-on-backdrop
      @hidden="resetModal"
    >
      <template #modal-header>
        <div class="fr-header">
          <div class="fr-header-icon">
            <feather-icon
              icon="SearchIcon"
              size="22"
            />
          </div>
          <div>
            <h2 class="fr-header-title">
              Find New Request
            </h2>
            <p class="fr-header-sub">
              Enter request details to search
            </p>
          </div>
        </div>
      </template>

      <!-- Form step -->
      <div
        v-if="step === 'form'"
        class="fr-step fr-step-form"
      >
        <div class="fr-field">
          <label class="fr-label">Request Number</label>
          <input
            v-model="form.requestNumber"
            type="text"
            class="fr-input"
            placeholder="Enter request number"
          >
        </div>
        <div class="fr-field">
          <label class="fr-label">Request Source</label>
          <select
            v-model="form.source"
            class="fr-input fr-select"
          >
            <option
              value=""
              disabled
            >
              Select Request Source
            </option>
            <option value="jeem">
              Jeem
            </option>
            <option value="saber-shipment">
              Saber Shipment
            </option>
            <option value="saber-conformity">
              Saber Conformity
            </option>
            <option value="saber-categorized-conformity">
              Saber Categorized Conformity
            </option>
          </select>
        </div>
        <div
          v-if="showRegionField"
          class="fr-field"
        >
          <label class="fr-label">Region</label>
          <select
            v-model="form.region"
            class="fr-input fr-select"
          >
            <option
              value=""
              disabled
            >
              Select Region
            </option>
            <option value="Dubai">
              Dubai
            </option>
            <option value="Manila">
              Manila
            </option>
            <option value="Hungary">
              Hungary
            </option>
          </select>
        </div>
      </div>

      <!-- Loading step -->
      <div
        v-if="step === 'loading'"
        class="fr-step fr-step-loading"
      >
        <div class="fr-loading-ring" />
        <p class="fr-loading-text">
          Searching for request…
        </p>
      </div>

      <!-- Success step -->
      <div
        v-if="step === 'success'"
        class="fr-step fr-step-result fr-step-success"
      >
        <div class="fr-result-icon fr-result-icon--success">
          <feather-icon
            icon="CheckCircleIcon"
            size="40"
          />
        </div>
        <h3 class="fr-result-title">
          Request found
        </h3>
        <p class="fr-result-text">
          The request has been loaded into the table.
        </p>
      </div>

      <!-- Error step -->
      <div
        v-if="step === 'error'"
        class="fr-step fr-step-result fr-step-error"
      >
        <div class="fr-result-icon fr-result-icon--error">
          <feather-icon
            icon="XCircleIcon"
            size="40"
          />
        </div>
        <h3 class="fr-result-title">
          No request found
        </h3>
        <p class="fr-result-text">
          {{ errorMessage }}
        </p>
      </div>

      <template #modal-footer>
        <template v-if="step === 'form'">
          <button
            type="button"
            class="fr-btn fr-btn--ghost"
            @click="showModal = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="fr-btn fr-btn--primary"
            :disabled="!canSubmit"
            @click="submitFind"
          >
            <feather-icon
              icon="SearchIcon"
              size="14"
              class="mr-50"
            />
            Find
          </button>
        </template>
        <template v-else>
          <button
            type="button"
            class="fr-btn fr-btn--primary"
            @click="showModal = false"
          >
            Close
          </button>
        </template>
      </template>
    </b-modal>
  </div>
</template>

<script>
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'

const SABER_SOURCES = ['saber-shipment', 'saber-conformity', 'saber-categorized-conformity']

export default {
  name: 'FindRequest',
  props: {
    getAllDataPass: {
      type: Function,
      required: true,
    },
    dataLoad: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      showModal: false,
      step: 'form', // form | loading | success | error
      form: {
        requestNumber: '',
        source: '',
        region: '',
      },
      errorMessage: 'No data found for this request.',
    }
  },
  computed: {
    showRegionField() {
      return SABER_SOURCES.includes(this.form.source)
    },
    canSubmit() {
      if (!this.form.requestNumber.trim() || !this.form.source) return false
      if (this.showRegionField && !this.form.region) return false
      return true
    },
  },
  methods: {
    openModal() {
      this.resetModal()
      this.showModal = true
    },
    resetModal() {
      this.step = 'form'
      this.form = { requestNumber: '', source: '', region: '' }
      this.errorMessage = 'No data found for this request.'
    },
    async submitFind() {
      if (!this.canSubmit) return

      this.step = 'loading'
      const payload = {
        code: this.form.requestNumber.trim(),
        source: this.form.source,
      }
      if (this.form.source !== 'jeem') {
        payload.region = this.form.region
      }

      try {
        await axios.post('https://gulftic-system.fci.group/api/urgent-request', payload)
        this.step = 'success'
        this.getAllDataPass(1)
      } catch (error) {
        this.step = 'error'
        this.errorMessage = decryptData(error.response.data.payload).message || 'No data found for this request.'
      }
    },
  },
}
</script>

<style scoped lang="scss">
.find-request-wrapper {
  display: inline-flex;
}

.find-request-btn {
  border-radius: 999px;
  padding: 0.35rem 0.95rem;
  font-size: 0.8rem;
  background: linear-gradient(135deg, #ec4899, #f97316);
  border: none;
  color: #ffffff !important;
  box-shadow: 0 8px 18px rgba(249, 115, 22, 0.35);
}

.find-request-btn:hover {
  background: linear-gradient(135deg, #db2777, #ea580c);
}
</style>

<style lang="scss">
/* Modal is portaled to body, so styles are unscoped */
$violet: #7c3aed;
$violet2: #5b21b6;
$indigo: #4f46e5;
$cyan: #0891b2;
$success: #059669;
$error: #dc2626;
$text: #1e293b;
$muted: #64748b;
$border: #e2e8f0;

.fr-modal .modal-dialog {
  border-radius: 1rem;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba($violet, 0.2), 0 8px 24px rgba(0, 0, 0, 0.12);
}

.fr-modal .modal-content {
  border: none;
  border-radius: 1rem;
}

.fr-modal-header {
  padding: 1.1rem 1.35rem;
  border-bottom: 1px solid $border;
  background: linear-gradient(135deg, rgba($violet, 0.08), rgba($cyan, 0.06));
}

.fr-header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.fr-header-icon {
  width: 48px;
  height: 48px;
  border-radius: 0.85rem;
  background: linear-gradient(135deg, $violet, $indigo);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.fr-header-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: $text;
  margin: 0 0 0.15rem 0;
}

.fr-header-sub {
  font-size: 0.8rem;
  color: $muted;
  margin: 0;
}

.fr-modal-body {
  min-height: 180px;
}

.fr-step {
  padding: 1.25rem 1.35rem;
}

.fr-field {
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.fr-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: $violet2;
  margin-bottom: 0.35rem;
}

.fr-input {
  width: 100%;
  padding: 0.55rem 0.85rem;
  font-size: 0.9rem;
  border: 1px solid $border;
  border-radius: 0.6rem;
  color: $text;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;

  &::placeholder {
    color: $muted;
  }

  &:focus {
    outline: none;
    border-color: $violet;
    box-shadow: 0 0 0 3px rgba($violet, 0.15);
  }
}

.fr-select {
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 0.75rem center;
  padding-right: 2.25rem;
}

.fr-step-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  gap: 1rem;
}

.fr-loading-ring {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 3px solid rgba($violet, 0.2);
  border-top-color: $violet;
  animation: fr-spin 0.7s linear infinite;
}

@keyframes fr-spin {
  to { transform: rotate(360deg); }
}

.fr-loading-text {
  font-size: 0.9rem;
  color: $muted;
  margin: 0;
  font-weight: 500;
}

.fr-step-result {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 2rem 1.5rem;
}

.fr-result-icon {
  margin-bottom: 0.85rem;

  &.fr-result-icon--success {
    color: $success;
  }

  &.fr-result-icon--error {
    color: $error;
  }
}

.fr-result-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: $text;
  margin: 0 0 0.35rem 0;
}

.fr-result-text {
  font-size: 0.88rem;
  color: $muted;
  margin: 0;
}

.fr-modal-footer {
  padding: 0.9rem 1.35rem;
  border-top: 1px solid $border;
  background: #fafbff;
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.fr-btn {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1.1rem;
  font-size: 0.85rem;
  font-weight: 600;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  transition: background 0.15s, transform 0.08s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.fr-btn--ghost {
  background: #fff;
  color: $muted;
  border: 1px solid $border;

  &:hover:not(:disabled) {
    background: #f1f5f9;
    color: $text;
  }
}

.fr-btn--primary {
  background: linear-gradient(135deg, $violet, $indigo);
  color: #fff;
  box-shadow: 0 4px 12px rgba($violet, 0.35);

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, $violet2, $violet);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba($violet, 0.4);
  }
}
</style>
